import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface DateInfo {
  totalTasks: number;
  completedTasks: number;
  pendingTasks: number;
  avgCompletion: number;
  status: 'complete' | 'partial' | 'pending';
}

interface CalendarViewProps {
  onDateSelect: (date: string) => void;
}

const CalendarView: React.FC<CalendarViewProps> = ({ onDateSelect }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dateData, setDateData] = useState<Record<string, DateInfo>>({});
  const [pendingSummary, setPendingSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();
      
      // Format dates without timezone conversion issues
      const formatDateLocal = (y: number, m: number, d: number) => {
        return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      };
      
      const startDate = formatDateLocal(year, month, 1);
      const lastDay = new Date(year, month + 1, 0).getDate(); // Get last day number
      const endDate = formatDateLocal(year, month, lastDay);

      const [overviewRes, pendingRes] = await Promise.all([
        apiService.getCalendarOverview(startDate, endDate),
        apiService.getPendingTasksSummary()
      ]);

      setDateData(overviewRes.dates || {});
      setPendingSummary(pendingRes.summary);
    } catch (err) {
      console.error('Failed to fetch calendar data:', err);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay };
  };

  const getDateColor = (dateStr: string) => {
    const info = dateData[dateStr];
    if (!info) return 'transparent';
    
    if (info.status === 'complete') return '#22c55e'; // Green
    if (info.status === 'partial') return '#f59e0b'; // Orange
    return '#ef4444'; // Red
  };

  const handleDateClick = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    onDateSelect(dateStr);
  };

  const navigateMonth = (delta: number) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + delta);
      return newDate;
    });
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  // Format today without timezone issues
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Pending Summary Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, var(--gradient-start, #667eea) 0%, var(--gradient-end, #764ba2) 100%)',
          borderRadius: '12px',
          padding: '1.5rem',
          color: 'white'
        }}>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Pending Tasks</div>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>{pendingSummary?.totalPending || 0}</div>
        </div>
        
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #f59e0b'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Days with Pending</div>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#f59e0b' }}>
            {pendingSummary?.daysWithPending || 0}
          </div>
        </div>
        
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #ef4444'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Oldest Pending</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#ef4444' }}>
            {(() => {
              if (!pendingSummary?.oldestPendingDate) return 'No pending tasks';
              
              const dateStr = pendingSummary.oldestPendingDate;
              // Handle both Date object and string formats
              const date = typeof dateStr === 'string' ? new Date(dateStr + 'T00:00:00') : new Date(dateStr);
              
              // Check if it's a valid date
              if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
                return 'No pending tasks';
              }
              
              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            })()}
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {/* Calendar Header */}
        <div style={{
          background: 'linear-gradient(135deg, var(--gradient-start, #667eea) 0%, var(--gradient-end, #764ba2) 100%)',
          padding: '1.5rem',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={() => navigateMonth(-1)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              fontSize: '1.25rem',
              cursor: 'pointer'
            }}
          >
            ◀
          </button>
          <h2 style={{ margin: 0, fontSize: '1.5rem' }}>
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h2>
          <button
            onClick={() => navigateMonth(1)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              fontSize: '1.25rem',
              cursor: 'pointer'
            }}
          >
            ▶
          </button>
        </div>

        {/* Legend */}
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          gap: '1.5rem',
          flexWrap: 'wrap',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#22c55e' }}></div>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>All complete</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#f59e0b' }}></div>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Partially done</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }}></div>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Pending</span>
          </div>
        </div>

        {/* Day Names */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          padding: '1rem',
          backgroundColor: '#f3f4f6'
        }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} style={{
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#4b5563'
            }}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '4px',
          padding: '1rem'
        }}>
          {/* Empty cells for starting day */}
          {Array.from({ length: startingDay }).map((_, i) => (
            <div key={`empty-${i}`} style={{ aspectRatio: '1', padding: '8px' }}></div>
          ))}
          
          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const year = currentMonth.getFullYear();
            const month = currentMonth.getMonth();
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const info = dateData[dateStr];
            const isToday = dateStr === today;
            const isSelected = dateStr === selectedDate;

            return (
              <div
                key={day}
                onClick={() => handleDateClick(day)}
                style={{
                  aspectRatio: '1',
                  padding: '8px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  position: 'relative',
                  background: isSelected ? 'var(--primary-color, #667eea)' : isToday ? '#f3f4f6' : 'white',
                  border: isToday ? '2px solid var(--primary-color, #667eea)' : '1px solid #e5e7eb',
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = isToday ? '#f3f4f6' : 'white';
                }}
              >
                <span style={{
                  fontWeight: isToday ? 700 : 500,
                  color: isSelected ? 'white' : '#1f2937',
                  fontSize: '1rem'
                }}>
                  {day}
                </span>
                
                {info && (
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: getDateColor(dateStr),
                    marginTop: '4px'
                  }}></div>
                )}
                
                {info && (
                  <span style={{
                    fontSize: '0.6rem',
                    color: isSelected ? 'rgba(255,255,255,0.8)' : '#9ca3af',
                    marginTop: '2px'
                  }}>
                    {info.completedTasks}/{info.totalTasks}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && dateData[selectedDate] && (
        <div style={{
          marginTop: '1.5rem',
          background: 'white',
          borderRadius: '12px',
          padding: '1.5rem',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 1rem', color: '#1f2937' }}>
            📅 {selectedDate}
          </h3>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Total Tasks</span>
              <div style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>{dateData[selectedDate].totalTasks}</div>
            </div>
            <div>
              <span style={{ color: '#22c55e', fontSize: '0.875rem' }}>Completed</span>
              <div style={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#22c55e' }}>
                {dateData[selectedDate].completedTasks}
              </div>
            </div>
            <div>
              <span style={{ color: '#ef4444', fontSize: '0.875rem' }}>Pending</span>
              <div style={{ fontWeight: 'bold', fontSize: '1.5rem', color: '#ef4444' }}>
                {dateData[selectedDate].pendingTasks}
              </div>
            </div>
            <div>
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>Completion</span>
              <div style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                {dateData[selectedDate].avgCompletion}%
              </div>
            </div>
          </div>
          <button
            onClick={() => onDateSelect(selectedDate)}
            style={{
              marginTop: '1rem',
              padding: '10px 20px',
              background: 'linear-gradient(135deg, var(--gradient-start, #667eea) 0%, var(--gradient-end, #764ba2) 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            View Tasks →
          </button>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
